import { useCallback, type Dispatch, type SetStateAction } from 'react';
import { tradingApi, type Order } from '../services/api';
import {
    buildOrderPriceFields,
    buildPlaceOrderPayload,
    getManualOrderValidationError,
} from '../components/ManualOrderEntry/orderPayload';
import type { ContractSelection } from '../components/ManualOrderEntry/contractSelection';

interface UseManualOrderSubmissionOptions {
    editingOrder: Order | null;
    unsupportedOrderType: string | null;
    symbol: string;
    action: 'BUY' | 'SELL';
    quantity: string;
    orderType: string;
    price: string;
    trailingAmount: string;
    trailType: 'amount' | 'percent';
    tif: 'DAY' | 'GTC';
    outsideRth: boolean;
    secType: string;
    strategyName: 'MANUAL' | 'NQMM';
    selectedContract: ContractSelection | null;
    setLoading: Dispatch<SetStateAction<boolean>>;
    setError: Dispatch<SetStateAction<string | null>>;
    setSuccess: Dispatch<SetStateAction<string | null>>;
    resetForm: () => void;
    onOrderModified?: () => void;
    onOrderSubmitted?: () => void;
}

export function useManualOrderSubmission({
    editingOrder,
    unsupportedOrderType,
    symbol,
    action,
    quantity,
    orderType,
    price,
    trailingAmount,
    trailType,
    tif,
    outsideRth,
    secType,
    strategyName,
    selectedContract,
    setLoading,
    setError,
    setSuccess,
    resetForm,
    onOrderModified,
    onOrderSubmitted,
}: UseManualOrderSubmissionOptions) {
    return useCallback(async () => {
        const validationError = getManualOrderValidationError({
            editingOrder: Boolean(editingOrder),
            unsupportedOrderType,
            symbol,
            quantity,
            orderType,
            price,
            tif,
            outsideRth,
            trailingAmount,
        });
        if (validationError) {
            setError(validationError);
            return;
        }

        const qtyNum = parseInt(quantity);
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const priceFields = buildOrderPriceFields(
                orderType,
                price,
                trailingAmount,
                trailType,
            );

            if (editingOrder) {
                const res = await tradingApi.modifyOrder(editingOrder.order_id, {
                    action,
                    quantity: qtyNum,
                    order_type: orderType,
                    ...priceFields,
                    tif,
                    outside_rth: outsideRth,
                });

                setSuccess(
                    res.message || `Order ${editingOrder.order_id} modification submitted.`,
                );
                window.dispatchEvent(new CustomEvent('trading-data-refresh'));
                resetForm();
                onOrderModified?.();
                return;
            }

            const orderPayload = buildPlaceOrderPayload(
                {
                    symbol,
                    action,
                    quantity: qtyNum,
                    orderType,
                    priceFields,
                    secType,
                    strategyName,
                    tif,
                    outsideRth,
                },
                selectedContract,
            );
            const res: any = await tradingApi.placeOrder(orderPayload);

            setSuccess(
                res.message || `${action} order for ${quantity} ${orderPayload.symbol} placed successfully.`,
            );
            window.dispatchEvent(new CustomEvent('trading-data-refresh'));
            resetForm();
            onOrderSubmitted?.();
        } catch (err: any) {
            console.warn(err);
            setError(err.message || 'Failed to place order.');
        } finally {
            setLoading(false);
        }
    }, [
        action,
        editingOrder,
        onOrderModified,
        onOrderSubmitted,
        outsideRth,
        orderType,
        price,
        quantity,
        resetForm,
        secType,
        selectedContract,
        setError,
        setLoading,
        setSuccess,
        strategyName,
        tif,
        trailingAmount,
        trailType,
        symbol,
        unsupportedOrderType,
    ]);
}
